import { Api, Fixtuur, ModularSofa } from '@fixtuur/backpack';
import * as TO from 'fp-ts/TaskOption';
import * as T from 'fp-ts/Task';
import { pipe } from 'fp-ts/function';
import { useSelector } from '../../../store/redux';
import * as Selectors from '../../../store/selectors';
import { fromQrCode, launchExploreFromQrCode } from '../url';
import { useCurrentSelections } from './useCurrentSelections';

const formatModularDataForHaymaker = (
    pieces: string[],
    globalSelections: ModularSofa.PropertySelections,
) => {
    const obj: { [key: string]: string | number | null } = {};

    pieces.map((item, idx) => {
        obj[`${idx}`] = item;
    });

    Object.keys(globalSelections).map(key => {
        obj[key] = globalSelections[key];
    });

    return obj;
};

export const generateModularSofaQrUrl = (
    currentUrl: string,
    serviceUrls: Api.ServiceConfig,
    modularConfiguratorId: string,
    pieces: string[],
    globalSelections: ModularSofa.PropertySelections,
): T.Task<string> => {
    const modularPiecesAndSelections = formatModularDataForHaymaker(
        pieces ?? [],
        globalSelections ?? {},
    );
    const postData = {
        configuratorId: modularConfiguratorId,
        selections: {
            properties: modularPiecesAndSelections,
            attachments: {},
        },
    };
    return pipe(
        () =>
            Api.Sharing.buildConfigurationLink('explore-config', currentUrl, postData, serviceUrls),
        T.map(url => {
            return fromQrCode(new URL(url)).toString();
        }),
    );
};

export const generateInspirationQrUrl = (currentUrl: string, productId: string): T.Task<string> =>
    T.of(
        launchExploreFromQrCode(
            // We need the iFrame parent's URL
            currentUrl,
            productId,
        ).toString(),
    );

export const generateConfigQrUrl = (
    currentUrl: string,
    serviceUrls: Api.ServiceConfig,
    configurationSpaceId: string,
    currentSelections: Fixtuur.Configuration.Selections.Selections,
    attachmentSelections: Record<string, string>,
) => {
    const postData = {
        configuratorId: configurationSpaceId,
        selections: {
            properties: Fixtuur.Configuration.Selections.toApi(currentSelections),
            attachments: attachmentSelections,
        },
    };
    return pipe(
        () =>
            Api.Sharing.buildConfigurationLink('explore-config', currentUrl, postData, serviceUrls),
        T.map(url => {
            return fromQrCode(new URL(url)).toString();
        }),
    );
};

export function useGenerateModularQrUrl(
    modularConfiguratorId: string,
    pieces: string[],
    globalSelections: ModularSofa.PropertySelections,
): TO.TaskOption<string> {
    const currentUrl = useSelector(Selectors.Core.currentUrl);
    const serviceUrls = useSelector(Selectors.Core.serviceConfig);

    if (!currentUrl || !serviceUrls) {
        return TO.none;
    }

    return TO.fromTask(
        generateModularSofaQrUrl(
            currentUrl,
            serviceUrls,
            modularConfiguratorId,
            pieces,
            globalSelections,
        ),
    );
}

export function useGenerateQrUrl(inspirationProductId: string | null): TO.TaskOption<string> {
    const currentUrl = useSelector(Selectors.Core.currentUrl);
    const serviceUrls = useSelector(Selectors.Core.serviceConfig);
    const configurationSpaceId = useSelector(
        Selectors.Initialisation.currentConfiguratorDefinition,
    )?.configurationSpaceId;
    const currentSelections = useCurrentSelections();
    const attachmentSelections = useSelector(Selectors.Selections.attachmentSelections);

    if (!currentUrl || !serviceUrls) {
        return TO.none;
    }

    return TO.fromTask(
        inspirationProductId !== null
            ? generateInspirationQrUrl(currentUrl, inspirationProductId)
            : configurationSpaceId && currentSelections && attachmentSelections
              ? generateConfigQrUrl(
                    currentUrl,
                    serviceUrls,
                    configurationSpaceId,
                    currentSelections,
                    attachmentSelections,
                )
              : TO.none,
    );
}
